/**
 * BooleanField -- Dynamic boolean toggle field.
 *
 * Renders a shadcn/ui Switch component with a label, optional help text,
 * tooltip, and error display. The switch is placed inline with the label
 * for a compact layout that matches common toggle patterns.
 */

import { HelpCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { DynamicFieldProps } from './index';

export function BooleanField({ field, value, onChange, error, readOnly }: DynamicFieldProps) {
  const switchId = `field-${field.name}`;
  const helpId = `${switchId}-help`;
  const errorId = `${switchId}-error`;

  const describedBy = [
    field.help_text ? helpId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Label htmlFor={switchId} className="cursor-pointer">
            {field.label}
            {field.required && (
              <>
                <span aria-hidden="true" className="text-destructive ml-1">*</span>
                <span className="sr-only">(required)</span>
              </>
            )}
          </Label>

          {field.tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle
                    className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                    aria-hidden="true"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-sm">{field.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <Switch
          id={switchId}
          checked={!!value}
          onCheckedChange={(checked) => onChange(checked)}
          disabled={readOnly}
          aria-required={field.required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
        />
      </div>

      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
